import {
  AcceptQuotationSuccess,
  cancelQuotationSuccess,
  deleteQuotationSuccess,
  DuplicateQuotationSuccess,
  UpdateQuotationDetailsSuccess,
  UpdateQuotationLinesSuccess,
  validateOrderSuccess,
} from 'src/app/features/quotations/store/quotations.actions';

/**
 * Every store action that makes a timeline card's rendered copy of an order
 * obsolete.
 *
 * The cards are drawn from the thread payload, which no order mutation writes
 * back to, so each of these has to trigger a refetch. The two `Update*` entries
 * are the side-by-side saving an edit: without them the header kept advertising
 * a stale "Last modified" until the user reloaded the page.
 *
 * Lives here rather than in `order-artifact.component.ts` so the grouped card
 * (WB-7598) can share the contract without importing its sibling component.
 */
export const ORDER_REFRESH_ACTIONS = [
  validateOrderSuccess,
  AcceptQuotationSuccess,
  cancelQuotationSuccess,
  deleteQuotationSuccess,
  DuplicateQuotationSuccess,
  UpdateQuotationLinesSuccess,
  UpdateQuotationDetailsSuccess,
] as const;

/** Any action of {@link ORDER_REFRESH_ACTIONS}, as dispatched. */
export type OrderRefreshAction = ReturnType<(typeof ORDER_REFRESH_ACTIONS)[number]>;

/**
 * The order a refresh action concerns, so a card only reacts to its own.
 *
 * Without it every mounted card reacts to every one of these: on a thread
 * carrying 17 orders, one side-by-side save made 17 cards emit `orderChanged`
 * and the parent dispatch `LoadConversationDetails` 17 times — 16 of them
 * aborted mid-flight by the effect's `switchMap`.
 *
 * `DuplicateQuotationSuccess` matches on `sourceId`, not `quotation.id`: the
 * payload is the NEW order, which no card renders yet, while the source is what
 * the user acted on and what the thread has to be refetched around.
 */
export function refreshedOrderId(action: OrderRefreshAction): number | undefined {
  switch (action.type) {
    case deleteQuotationSuccess.type:
      return action.id;
    case DuplicateQuotationSuccess.type:
      return action.sourceId;
    case validateOrderSuccess.type:
    case UpdateQuotationLinesSuccess.type:
    case UpdateQuotationDetailsSuccess.type:
      return action.orderId;
    default:
      return action.quotation?.id;
  }
}
